import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { CategoryIcon, CategoryIconVariants, P, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore, useScreenSize } from '../../common'
import { CategoryGuids } from '../../core/constants'
import Donut from '../../common/components/charts/Donut'
import { useAriaLive } from '../../common/hooks/useAriaLive'

import type { SpendingData } from '../utils/SpendingData'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface SpendingDonutProps {
  data: SpendingData
  isIncome?: boolean
  onSelected: (id: string) => void
  onViewTransactions?: (id: string) => void
  selectedId: string
  size?: number
  totalLabel: string
  variant?: 'mini' | 'full'
}

const SpendingDonut: React.FC<SpendingDonutProps> = ({
  data,
  isIncome = false,
  onSelected,
  onViewTransactions,
  selectedId,
  size = 150,
  totalLabel,
  variant = 'mini',
}) => {
  const { spending: spendingCopy } = useGlobalCopyStore()
  const theme = useTheme()
  const { isTablet } = useScreenSize()
  const { announce, ariaLive } = useAriaLive()

  const selectedCategory = React.useMemo(
    () => (selectedId ? data.categoryData.find((cd) => cd.guid === selectedId) : undefined),
    [selectedId],
  )

  // Announce category selection to screen readers
  React.useEffect(() => {
    if (selectedCategory) {
      const amount = formatCurrency(selectedCategory.amount, variant === 'mini' ? '0,0' : '0,0.00')
      announce(`${selectedCategory.name}: ${amount}`)
    }
  }, [selectedCategory, announce, variant])

  const donutData =
    data.donutData.length > 0
      ? data.donutData
      : [{ id: '0', color: theme.palette.divider, value: 100 }]

  const handleViewTransactions = () => {
    onViewTransactions?.(selectedId)
  }

  const iconSize = isTablet ? 32 : 48
  return (
    <Box
      aria-label={spendingCopy.chart_description}
      height={size}
      position="relative"
      role="group"
      width={size}
    >
      {ariaLive}
      <Donut data={donutData} onClick={onSelected} selectedId={selectedId} size={size}>
        <Stack alignItems="center" height="100%" justifyContent="center" width="100%">
          <Stack alignItems="center" justifyContent="center" minHeight={size} minWidth={size}>
            {selectedCategory && (
              <Stack
                alignItems="center"
                gap={variant === 'mini' ? 0 : 8}
                mb={variant === 'mini' ? 0 : 8}
                mt={variant === 'mini' ? 0 : -16}
                sx={
                  variant === 'mini'
                    ? { gap: 0, mb: 0 }
                    : { gap: { xs: 4, xl: 8 }, mb: { xs: 4, xl: 8 } }
                }
              >
                <Box aria-hidden="true" role="presentation">
                  <CategoryIcon
                    categoryGuid={isIncome ? CategoryGuids.INCOME : selectedCategory.guid}
                    size={variant === 'mini' ? 24 : iconSize}
                    variant={CategoryIconVariants.Transparent}
                  />
                </Box>
                <Text
                  color="text.secondary"
                  noWrap={true}
                  variant={variant === 'mini' ? 'tiny' : 'body1'}
                >
                  {selectedCategory ? selectedCategory.name : totalLabel}
                </Text>
              </Stack>
            )}

            {!selectedCategory && variant === 'full' && (
              <Text bold={true} color="text.secondary" sx={{ mb: { xs: 4, xl: 8 } }}>
                {totalLabel}
              </Text>
            )}

            <P variant={variant === 'mini' ? 'h2' : 'h1'}>
              {selectedCategory
                ? formatCurrency(selectedCategory.amount, variant === 'mini' ? '0,0' : '0,0.00')
                : formatCurrency(data.totalAmount, variant === 'mini' ? '0,0' : '0,0.00')}
            </P>
            {!selectedCategory && variant === 'mini' && (
              <Text color="text.secondary" noWrap={true} variant="tiny">
                {totalLabel}
              </Text>
            )}

            {variant === 'full' && (
              <Button
                aria-label={`View ${selectedCategory?.name || ''} Transactions`}
                disabled={data.categoryData.length === 0}
                onClick={handleViewTransactions}
                sx={{ mt: { xl: 4, xs: 0 }, zIndex: 1 }}
                variant="text"
              >
                {spendingCopy.view_transactions}
              </Button>
            )}
          </Stack>
        </Stack>
      </Donut>
    </Box>
  )
}

export default observer(SpendingDonut)
